// ── Ten or Nothing: the war room. A holographic floor receding into a night
//    skyline, radar sweep, scan lines and drifting telemetry. ──

import { useMemo, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { useParallax, useAmbientBeat, usePrefersReducedMotion, seeded } from '../../lib/battle3d';

const Skyline = ({ tone }) => {
  const towers = useMemo(() => {
    const rnd = seeded(5150);
    let x = -20;
    const out = [];
    while (x < 1020) {
      const w = 26 + rnd() * 56;
      const h = 30 + rnd() * 120;
      out.push({ x, w, h, lights: Math.floor(rnd() * 5) + 2, seed: rnd() });
      x += w + 4 + rnd() * 14;
    }
    return out;
  }, []);

  return (
    <svg viewBox="0 0 1000 170" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
      {towers.map((t, i) => (
        <g key={i}>
          <rect x={t.x} y={170 - t.h} width={t.w} height={t.h} fill="#01040c" />
          {Array.from({ length: t.lights }, (_, j) => (
            <motion.rect
              key={j}
              x={t.x + 5 + (j % 3) * (t.w / 3.4)}
              y={170 - t.h + 9 + Math.floor(j / 3) * 15}
              width="4" height="6" fill={tone}
              animate={{ opacity: [0.18, 0.75, 0.3, 0.6, 0.18] }}
              transition={{ duration: 3 + t.seed * 4 + j * 0.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
          {/* beacon on the tallest spires */}
          {t.h > 120 && (
            <motion.circle
              cx={t.x + t.w / 2} cy={170 - t.h - 3} r="2.4" fill="#f87171"
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ duration: 1.8, repeat: Infinity, delay: t.seed * 2 }}
            />
          )}
        </g>
      ))}
    </svg>
  );
};

const CommandRoom = ({ mobile = false, dimmed = false }) => {
  const reduced = usePrefersReducedMotion();
  const { px, py } = useParallax(!reduced);
  const [ping, setPing] = useState(0);

  useAmbientBeat(() => setPing(Date.now()), { min: 9000, max: 18000, enabled: !reduced });

  const skyX = useTransform(px, (v) => v * -8);
  const skyY = useTransform(py, (v) => v * -3.6);
  const cityX = useTransform(px, (v) => v * -26);
  const cityY = useTransform(py, (v) => v * -11);
  const floorX = useTransform(px, (v) => v * -64);
  const floorY = useTransform(py, (v) => v * -28);

  const motes = useMemo(() => {
    const n = reduced ? 0 : mobile ? 14 : 30;
    const rnd = seeded(8080);
    return Array.from({ length: n }, () => ({
      x: rnd() * 100, size: 1 + rnd() * 2.6, dur: 7 + rnd() * 9, delay: rnd() * 9, drift: (rnd() - 0.5) * 90,
    }));
  }, [mobile, reduced]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {/* Night base */}
      <div style={{ position: 'absolute', inset: 0, background: '#01050e' }} />

      {/* Upper atmosphere */}
      <motion.div style={{
        position: 'absolute', top: '-8%', left: '-8%', right: '-8%', height: '78%',
        x: skyX, y: skyY,
        background: `radial-gradient(ellipse 100% 55% at 50% 100%, rgba(40,110,255,0.26) 0%, transparent 70%),
                     linear-gradient(to bottom, #01030a 0%, #030a1e 55%, #06183c 100%)`,
      }} />

      {/* Cold stars */}
      {!reduced && Array.from({ length: mobile ? 20 : 42 }, (_, i) => {
        const rnd = seeded(900 + i);
        const a = rnd(), b = rnd(), c = rnd();
        return (
          <motion.div key={i}
            animate={{ opacity: [0.1, 0.7, 0.1] }}
            transition={{ duration: 2.5 + c * 4, repeat: Infinity, delay: i * 0.21 }}
            style={{ position: 'absolute', left: `${a * 100}%`, top: `${b * 28}%`, width: 1.4, height: 1.4, borderRadius: '50%', background: '#cfe3ff' }}
          />
        );
      })}

      {/* Horizon haze */}
      <div style={{
        position: 'absolute', left: '-10%', right: '-10%', bottom: '58%', height: '14%',
        background: 'radial-gradient(ellipse 60% 100% at 50% 100%, rgba(56,132,255,0.34) 0%, transparent 74%)',
        filter: 'blur(18px)',
      }} />

      {/* Skyline */}
      <motion.div style={{ position: 'absolute', left: '-6%', right: '-6%', bottom: '61%', height: mobile ? '13%' : '19%', x: cityX, y: cityY }}>
        <Skyline tone="#7fb0ff" />
      </motion.div>

      {/* Holographic floor, clipped to the ground */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '61%', overflow: 'hidden' }}>
        <motion.div style={{
          position: 'absolute', left: '-25%', right: '-25%', top: 0, height: '190%',
          x: floorX, y: floorY,
          transform: 'perspective(760px) rotateX(61deg)', transformOrigin: 'center top',
          backgroundImage: `
            repeating-linear-gradient(to right, rgba(80,150,255,0.26) 0 1px, transparent 1px 64px),
            repeating-linear-gradient(to bottom, rgba(80,150,255,0.2) 0 1px, transparent 1px 44px),
            radial-gradient(ellipse 50% 55% at 50% 0%, rgba(30,90,220,0.32) 0%, transparent 66%),
            linear-gradient(to bottom, rgba(2,6,16,0.88), rgba(1,2,6,0.99))`,
        }} />
      </div>

      {/* Radar sweep behind the table */}
      {!reduced && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', left: '50%', top: '52%', width: mobile ? 340 : 620, height: mobile ? 340 : 620,
            marginLeft: mobile ? -170 : -310, marginTop: mobile ? -170 : -310, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, rgba(90,170,255,0.16) 0deg, transparent 52deg)',
            opacity: 0.55,
          }}
        />
      )}
      {[0.42, 0.7, 1].map((s, i) => (
        <div key={i} style={{
          position: 'absolute', left: '50%', top: '52%',
          width: (mobile ? 340 : 620) * s, height: (mobile ? 340 : 620) * s,
          marginLeft: -((mobile ? 340 : 620) * s) / 2, marginTop: -((mobile ? 340 : 620) * s) / 2,
          borderRadius: '50%', border: '1px solid rgba(90,170,255,0.10)',
        }} />
      ))}

      {/* System ping */}
      <motion.div key={ping} initial={{ opacity: 0 }}
        animate={ping ? { opacity: [0, 0.32, 0] } : {}}
        transition={{ duration: 1.1 }}
        style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 60%, rgba(120,190,255,0.5), transparent 58%)' }} />

      {/* Telemetry motes */}
      {motes.map((m, i) => (
        <motion.div key={i}
          initial={{ opacity: 0 }}
          animate={{ y: ['0vh', '-64vh'], x: [0, m.drift], opacity: [0, 0.7, 0.5, 0] }}
          transition={{ duration: m.dur, repeat: Infinity, delay: m.delay, ease: 'linear' }}
          style={{
            position: 'absolute', left: `${m.x}%`, bottom: '-2%', width: m.size, height: m.size,
            borderRadius: '50%', background: '#8ec0ff', boxShadow: `0 0 ${m.size * 4}px #4f86ff`,
          }} />
      ))}

      {/* Scan lines */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.35,
        backgroundImage: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.022) 0 1px, transparent 1px 3px)',
      }} />

      {/* Grade + vignette */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 44%, rgba(0,0,0,0.42) 74%, rgba(0,0,0,0.7) 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, transparent 38%, rgba(0,0,0,0.5) 76%, rgba(0,0,0,0.84) 100%)' }} />
      {dimmed && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />}
    </div>
  );
};

export default CommandRoom;
